import {
    createContext,
    PropsWithChildren,
    ReactNode,
    useCallback,
    useRef,
    useState,
} from 'react';
import { Box, IconButton, Slider, Tooltip } from '@mui/material';
import classNames from 'classnames';

import { Icon } from '../../shared/Icon';
import { Button } from '../Button';
import { Input } from '../Input';
import { Listbox, ListboxItem, ListboxProps } from '../Listbox/Listbox';
import { TimelineGrid } from './TimelineGrid';
import {
    useTimelineNavigation,
    useTimelineOperations,
    useTimelineTransforms,
} from './TimelineHooks';
import { TimelineItem } from './TimelineItem';
import { TimelineMarker } from './TimelineMarker';
import { getOverlappingTimelineItems } from './TimelineUtils';

import { useOnClickOutside } from '../../hooks/onClickOutside';

import { assert } from '../../utils/assert';
import { toHTMLDate } from '../../utils/dates';

import styles from './Timeline.module.scss';

const HOUR_IN_MS = 1000 * 60 * 60;

export interface TimelineItemData {
    start: Date;
    end: Date;
    lane: number;
}
export interface TimelineItemRenderData extends TimelineItemData {
    id: string;
    handleColor: string;
    render: () => ReactNode;
    onChange?: (item: Partial<TimelineItemData>) => void;
}
interface CreateItemData {
    pointerDown: number;
    pointerCurrent: number;
    lane: number;
}

export interface TimelineContext {
    timeToPosition: (ms: number) => number;
    positionToTime: (position: number) => number;
    positionToLane: (position: number) => number;
    laneToPosition: (lane: number) => number;
    isVisible: (from: number, to?: number) => boolean;
    pixelsPerMs: number;
    laneHeight: number;
    lowerBound: number;
    upperBound: number;
    timescale: Timescale;
}

export const TimelineContext = createContext<TimelineContext | null>(null);

export interface TimelineProps extends PropsWithChildren {
    className?: string;
    toolbarClassName?: string;
    toolbarChildren?: ReactNode;
    lanes: number;
    laneHeight: number;
    timelineItems: TimelineItemRenderData[];
    onCreate?: (timelineItem: TimelineItemData) => void;
    onDelete?: (id: string) => void;
    onDuplicate?: (id: string) => void;
    onEdit?: (id: string) => void;
}

export interface Timescale {
    label: string;
    stepSize: number;
    roundTo: (date: Date) => Date;
}

export function Timeline({
    className,
    toolbarClassName,
    toolbarChildren,
    lanes,
    laneHeight,
    timelineItems,
    children,
    onCreate,
    onDelete,
    onDuplicate,
    onEdit,
}: TimelineProps) {
    const containerRef = useRef<HTMLDivElement | null>(null);
    const {
        lowerBound,
        upperBound,
        timescale,
        gridTimescale,
        scrollTo,
        pixelsPerMs,
        scale,
        setScale,
        MIN_SCALE,
        MAX_SCALE,
    } = useTimelineNavigation({
        lanes,
        laneHeight,
        timelineContainer: containerRef.current,
    });
    const {
        isVisible,
        laneToPosition,
        positionToLane,
        positionToTime,
        timeToPosition,
    } = useTimelineTransforms({
        lanes,
        laneHeight,
        pixelsPerMs,
        lowerBound,
        upperBound,
    });

    const { onMove, onStartMove, onEndMove, onStartExtend, onEndExtend } =
        useTimelineOperations(timelineItems, timescale);

    const [createItem, setCreateItem] = useState<CreateItemData | null>(null);
    const createTimelineItem = createItem
        ? createItemToTimelineItem(createItem)
        : null;

    const currentDateRef = useRef<HTMLInputElement>(null);

    const [contextMenu, setContextMenu] = useState<null | {
        position: { x: number; y: number };
        data: { id: string };
    }>(null);

    const selectedTimelineItem = contextMenu
        ? timelineItems.find((item) => item.id === contextMenu.data.id)
        : undefined;

    const handleZoom = (newScale: number) => {
        setScale(newScale);
    };

    return (
        <div className={classNames(className)}>
            <div className={classNames(toolbarClassName, styles.toolbar)}>
                <strong>Go to:</strong>
                <Button
                    size="small"
                    onClick={() => {
                        scrollTo(Date.now());
                    }}
                >
                    Today
                </Button>
                <Input
                    type="date"
                    size="small"
                    inputClassName={styles.gotoDateInput}
                    onChange={(e) => {
                        scrollTo(e.target.valueAsNumber);
                    }}
                    ref={currentDateRef}
                />
                <Box className={styles.zoomControls}>
                    <Tooltip title="Zoom out">
                        <IconButton
                            size="small"
                            onClick={() =>
                                handleZoom(Math.max(scale - 0.1, MIN_SCALE))
                            }
                        >
                            <Icon name="zoom_out" style={{ fontSize: 21 }} />
                        </IconButton>
                    </Tooltip>
                    <Slider
                        min={MIN_SCALE}
                        max={MAX_SCALE}
                        step={0.01}
                        value={scale}
                        onChange={(_, value) => handleZoom(value as number)}
                        style={{ width: 100, margin: '0 8px' }}
                        size="small"
                        aria-label="Zoom"
                    />
                    <Tooltip title="Zoom in">
                        <IconButton
                            size="small"
                            onClick={() =>
                                handleZoom(Math.min(scale + 0.1, MAX_SCALE))
                            }
                        >
                            <Icon name="zoom_in" style={{ fontSize: 21 }} />
                        </IconButton>
                    </Tooltip>
                </Box>

                {toolbarChildren}
            </div>
            <TimelineContext.Provider
                value={{
                    laneHeight,
                    positionToLane,
                    laneToPosition,
                    positionToTime,
                    timeToPosition,
                    lowerBound,
                    upperBound,
                    isVisible,
                    pixelsPerMs,
                    timescale,
                }}
            >
                <div
                    ref={containerRef}
                    style={{
                        height: (lanes + 1) * laneHeight,
                    }}
                    className={classNames(styles.container)}
                    onScroll={(e) => {
                        const target = e.target;
                        assert(target instanceof HTMLElement);

                        if (currentDateRef.current) {
                            const scrollCenter =
                                target.scrollLeft + target.clientWidth * 0.52; // 0.52 instead of 0.50 to have some padding due to rounding errors
                            const scrollCenterDate = new Date(
                                positionToTime(scrollCenter),
                            );
                            currentDateRef.current.valueAsDate =
                                scrollCenterDate;
                        }
                    }}
                    onPointerDown={(e) => {
                        if (e.button !== 0) {
                            // Only trigger on left mouse button
                            return;
                        }
                        if (e.currentTarget !== e.target) {
                            return;
                        }
                        e.currentTarget.setPointerCapture(e.pointerId);

                        const lane = positionToLane(
                            e.nativeEvent.offsetY - laneHeight,
                        );
                        const time = positionToTime(
                            e.currentTarget.scrollLeft + e.nativeEvent.offsetX,
                        );
                        const createItem = {
                            lane,
                            pointerDown: time,
                            pointerCurrent: time,
                        };
                        if (
                            getOverlappingTimelineItems(
                                createItemToTimelineItem(createItem),
                                timelineItems,
                            ).length === 0
                        ) {
                            setCreateItem(createItem);
                        }
                    }}
                    onPointerUp={(e) => {
                        if (e.currentTarget !== e.target) {
                            return;
                        }
                        if (
                            createTimelineItem === null ||
                            createItem === null
                        ) {
                            return;
                        }
                        e.currentTarget.releasePointerCapture(e.pointerId);
                        setCreateItem(null);

                        if (
                            Math.abs(
                                createItem.pointerDown -
                                    createItem.pointerCurrent,
                            ) < HOUR_IN_MS
                        ) {
                            return;
                        }
                        onCreate?.({
                            lane: createTimelineItem.lane,
                            start: timescale.roundTo(createTimelineItem.start),
                            end: timescale.roundTo(createTimelineItem.end),
                        });
                    }}
                    onPointerMove={(e) => {
                        if (e.currentTarget !== e.target) {
                            return;
                        }
                        const time = positionToTime(
                            e.currentTarget.scrollLeft + e.nativeEvent.offsetX,
                        );

                        if (createItem) {
                            setCreateItem({
                                lane: createItem.lane,
                                pointerDown: createItem.pointerDown,
                                pointerCurrent: time,
                            });
                        }
                    }}
                >
                    {children}
                    <TimelineGrid
                        timescale={gridTimescale ?? timescale}
                        subTimescale={gridTimescale ? timescale : undefined}
                    />
                    {timelineItems.map((item) => (
                        <TimelineItem
                            key={item.id}
                            value={item}
                            onContextMenu={(e) => {
                                e.preventDefault();
                                setContextMenu({
                                    position: {
                                        x: e.clientX,
                                        y: e.clientY,
                                    },
                                    data: { id: item.id },
                                });
                            }}
                            onMove={(value) => onMove(item, value)}
                            onStartMove={(newStart) =>
                                onStartMove(item, newStart)
                            }
                            onEndMove={(newEnd) => onEndMove(item, newEnd)}
                            onEndExtend={() => onEndExtend(item)}
                            onStartExtend={() => onStartExtend(item)}
                            handleColor={item.handleColor}
                        >
                            {item.render()}
                        </TimelineItem>
                    ))}
                    {createTimelineItem && (
                        <TimelineItem
                            value={{
                                lane: createTimelineItem.lane,
                                start: timescale.roundTo(
                                    createTimelineItem.start,
                                ),
                                end: timescale.roundTo(createTimelineItem.end),
                            }}
                        />
                    )}
                    <TimelineMarker time={new Date()} />

                    {contextMenu && selectedTimelineItem && (
                        <TimelineItemContextMenu
                            timelineItem={selectedTimelineItem}
                            onClose={() => setContextMenu(null)}
                            onDelete={() => onDelete?.(selectedTimelineItem.id)}
                            onDuplicate={
                                onDuplicate
                                    ? () => onDuplicate(selectedTimelineItem.id)
                                    : undefined
                            }
                            onEdit={
                                onEdit
                                    ? () => onEdit(selectedTimelineItem.id)
                                    : undefined
                            }
                            onUpdateStart={(newStart) => {
                                onStartMove(selectedTimelineItem, newStart);
                            }}
                            onUpdateEnd={(newEnd) => {
                                onEndMove(selectedTimelineItem, newEnd);
                            }}
                            position={contextMenu.position}
                        />
                    )}
                </div>
            </TimelineContext.Provider>
        </div>
    );
}

function createItemToTimelineItem(
    createItem: CreateItemData,
): TimelineItemData {
    return {
        lane: createItem.lane,
        start: new Date(
            Math.min(createItem.pointerDown, createItem.pointerCurrent),
        ),
        end: new Date(
            Math.max(createItem.pointerDown, createItem.pointerCurrent),
        ),
    };
}

interface TimelineItemContextMenuProps extends ListboxProps {
    position: { x: number; y: number };
    timelineItem: TimelineItemData & { id: string };
    onClose?: () => void;
    onDelete?: () => void;
    onDuplicate?: () => void;
    onEdit?: () => void;
    onUpdateStart?: (date: Date) => void;
    onUpdateEnd?: (date: Date | null) => void;
}
function TimelineItemContextMenu({
    position,
    timelineItem,
    onDelete,
    onDuplicate,
    onEdit,
    onClose,
    onUpdateStart,
    onUpdateEnd,
    ...listBoxProps
}: TimelineItemContextMenuProps) {
    const contextMenuRef = useRef<HTMLDivElement>(null);
    useOnClickOutside(
        contextMenuRef,
        useCallback(() => {
            onClose?.();
        }, [onClose]),
    );

    const utcOffset = -new Date().getTimezoneOffset() / 60;
    const utcLabel = `UTC ${utcOffset >= 0 ? '+' : ''}${utcOffset}`;

    return (
        <Listbox
            ref={contextMenuRef}
            style={{
                position: 'fixed',
                left: position.x,
                top: position.y,
                zIndex: 100,
            }}
            spacing={0}
            className={styles.contextMenu}
            {...listBoxProps}
        >
            <ListboxItem
                endContent={
                    <Input
                        aria-label="Start time"
                        size="small"
                        type="datetime-local"
                        value={toHTMLDate(timelineItem.start)}
                        onChange={(e) => {
                            onUpdateStart?.(new Date(e.target.valueAsNumber));
                        }}
                    />
                }
            >
                Start Time ({utcLabel})
            </ListboxItem>
            <ListboxItem
                endContent={
                    <Input
                        aria-label="End time"
                        size="small"
                        type="datetime-local"
                        value={
                            timelineItem.end ? toHTMLDate(timelineItem.end) : ''
                        }
                        onChange={(e) => {
                            onUpdateEnd?.(new Date(e.target.valueAsNumber));
                        }}
                    />
                }
            >
                End Time ({utcLabel})
            </ListboxItem>
            <ListboxItem className={styles.contextMenuActions}>
                <div className={styles.contextMenuActionsLeft}>
                    {onDuplicate && (
                        <button
                            aria-label="Duplicate publication"
                            className={styles.contextMenuAction}
                            onClick={() => {
                                onDuplicate();
                                onClose?.();
                            }}
                        >
                            <svg
                                aria-hidden="true"
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 24 24"
                                width="14"
                                height="14"
                            >
                                <path
                                    fill="currentColor"
                                    d="M6.569 2.25h6.362c.604 0 1.102 0 1.508.033c.421.035.809.109 1.173.294a3 3 0 0 1 1.311 1.311c.186.364.26.752.294 1.173c.033.406.033.904.033 1.508v.181h.4c.503 0 .918 0 1.257.023c.352.024.678.076.991.205a3 3 0 0 1 1.624 1.624c.13.313.18.639.205.99c.023.34.023.755.023 1.257v.052c0 .502 0 .917-.023 1.256a3.2 3.2 0 0 1-.189.95a12.43 12.43 0 0 1-8.236 8.37l-.189.059a3.1 3.1 0 0 1-.956.19c-.34.024-.754.024-1.256.024h-.052c-.502 0-.917 0-1.256-.023c-.352-.024-.678-.076-.991-.205a3 3 0 0 1-1.624-1.624c-.13-.313-.18-.639-.205-.99c-.023-.34-.023-.755-.023-1.257v-.401h-.18c-.604 0-1.103 0-1.509-.033c-.421-.035-.809-.108-1.173-.294a3 3 0 0 1-1.311-1.311c-.185-.364-.26-.752-.294-1.173c-.033-.406-.033-.904-.033-1.508V6.569c0-.604 0-1.102.033-1.508c.035-.421.109-.809.294-1.173a3 3 0 0 1 1.311-1.311c.364-.185.752-.26 1.173-.294c.406-.033.904-.033 1.508-.033M8.25 17.625c0 .534 0 .898.02 1.18c.018.276.053.419.094.519a1.5 1.5 0 0 0 .812.812c.1.041.243.076.519.094c.282.02.646.02 1.18.02s.898 0 1.18-.02c.276-.018.419-.053.519-.094a1.5 1.5 0 0 0 .812-.812c.041-.1.075-.243.094-.519c.02-.282.02-.646.02-1.18v-.026c0-.502 0-.917.023-1.256c.024-.352.076-.678.205-.991a3 3 0 0 1 1.624-1.624c.313-.13.639-.18.99-.205c.34-.023.755-.023 1.257-.023h.026c.534 0 .898 0 1.18-.02c.276-.019.419-.053.519-.094a1.5 1.5 0 0 0 .812-.812c.041-.1.076-.243.094-.519c.02-.282.02-.646.02-1.18s0-.898-.02-1.18c-.018-.276-.053-.419-.094-.519a1.5 1.5 0 0 0-.812-.812c-.1-.041-.243-.076-.519-.094c-.282-.02-.646-.02-1.18-.02H11.1c-.642 0-1.08 0-1.417.028c-.329.027-.497.076-.614.135a1.5 1.5 0 0 0-.656.656c-.06.117-.108.285-.135.614c-.027.338-.028.775-.028 1.417z"
                                />
                            </svg>
                            <span className={styles.contextMenuActionLabel}>
                                Duplicate
                            </span>
                        </button>
                    )}
                    {onEdit && (
                        <button
                            aria-label="Edit publication"
                            className={styles.contextMenuAction}
                            onClick={() => {
                                onEdit();
                                onClose?.();
                            }}
                        >
                            <svg
                                aria-hidden="true"
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 24 24"
                                width="14"
                                height="14"
                            >
                                <path
                                    fill="currentColor"
                                    d="M3 21v-4.25L16.2 3.575q.3-.275.663-.425t.762-.15t.775.15t.65.45L20.425 5q.3.275.438.65T21 6.4q0 .4-.137.763t-.438.662L7.25 21zM17.6 7.8L19 6.4L17.6 5l-1.4 1.4z"
                                />
                            </svg>
                            <span className={styles.contextMenuActionLabel}>
                                Edit
                            </span>
                        </button>
                    )}
                </div>
                <button
                    aria-label="Delete publication"
                    className={`${styles.contextMenuAction} ${styles.contextMenuActionDelete}`}
                    onClick={() => {
                        onDelete?.();
                        onClose?.();
                    }}
                >
                    <svg
                        aria-hidden="true"
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 512 512"
                        width="14"
                        height="14"
                    >
                        <rect
                            width="448"
                            height="80"
                            x="32"
                            y="48"
                            fill="currentColor"
                            rx="32"
                            ry="32"
                        />
                        <path
                            fill="currentColor"
                            d="M74.45 160a8 8 0 0 0-8 8.83l26.31 252.56a1.5 1.5 0 0 0 0 .22A48 48 0 0 0 140.45 464h231.09a48 48 0 0 0 47.67-42.39v-.21l26.27-252.57a8 8 0 0 0-8-8.83Zm248.86 180.69a16 16 0 1 1-22.63 22.62L256 318.63l-44.69 44.68a16 16 0 0 1-22.63-22.62L233.37 296l-44.69-44.69a16 16 0 0 1 22.63-22.62L256 273.37l44.68-44.68a16 16 0 0 1 22.63 22.62L278.62 296Z"
                        />
                    </svg>
                    <span className={styles.contextMenuActionLabel}>
                        Delete
                    </span>
                </button>
            </ListboxItem>
        </Listbox>
    );
}
